import { useState } from 'react';
import {
  IconContainer,
  Panel,
  RadioInput,
  Text,
  Tooltip,
  //RadioGroup as RadioChoise,
} from '@epam/promo';

import { useTaskStore } from 'store/task';
import { isBasicValidationType } from 'store/task/task-validation-helper';
import { TagsInput } from 'components/common/TagsInput/TagsInput';
import {
  Task,
  VALIDATION_TOOLTIP_TEXT,
  VALIDATION_TYPE,
  ValidationTooltipText,
  ValidationType,
} from 'models/task.model';
import css from '../FormBody.module.scss';
import { TAG_MAX_LENGTH } from './constants';
import { InlineLabelInput } from '../../../common';
import { QuestionValidator } from './QuestionsValidator';

export const RadioGroup = ({ task }: { task: Task }) => {
  const { answers, validator, status } = task;
  const correctAnswers = answers?.correctAnswers ?? [];
  const {
    errors,
    actions: { setDraftAnswer, setDraftValidator },
  } = useTaskStore((state) => state);
  const [inputValue, setInputValue] = useState('');

  const addCorrectAnswer = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmed = inputValue.trim();

    const isValid =
      trimmed &&
      !correctAnswers.includes(trimmed) &&
      event.key === 'Enter' &&
      trimmed.length <= TAG_MAX_LENGTH;

    if (isValid) {
      setDraftAnswer({
        ...answers,
        correctAnswers: [...correctAnswers, trimmed],
      });
      setInputValue('');
    }

    const isBackspaceWithEmptyInput =
      event.key === 'Backspace' && inputValue === '';

    if (isBackspaceWithEmptyInput) {
      const tagValue = correctAnswers[correctAnswers.length - 1];

      if (tagValue) {
        const nextTags = correctAnswers.slice(0, -1);

        setDraftAnswer({
          ...answers,
          correctAnswers: nextTags,
        });
        setInputValue(tagValue);
      }
    }
  };

  const removeCorrectAnswer = (tag: string): void => {
    const nextTags = correctAnswers.filter((answer) => answer !== tag);

    setDraftAnswer({
      ...answers,
      correctAnswers: nextTags,
    });
  };

  const isNotAvailableStatus =
    status === 'PENDING_APPROVAL' || status === 'APPROVED';

  const hasAnswerErrors = !!errors.answers;

  return (
    <InlineLabelInput isRequired align="top" label="Validator">
      <Text cx={css.form__text_italic}>
        *Choose one of the options to validate user&apos;s answers in the bot.
      </Text>

      <Panel cx={css.radio__inputs}>
        {Object.keys(VALIDATION_TYPE).map((validation) => (
          <div key={validation}>
            <div className={css.radio__label}>
              <RadioInput
                cx={{
                  [css['radio-input__error']]:
                    errors.answers && validator === validation,
                  [css.form__input_disabled]: isNotAvailableStatus,
                }}
                isDisabled={isNotAvailableStatus}
                isRequired={hasAnswerErrors}
                label={
                  <div className={css.radio__label}>
                    <span className={css.radio__label__text}>
                      {VALIDATION_TYPE[validation as ValidationType]}
                    </span>
                    <Tooltip
                      content={
                        VALIDATION_TOOLTIP_TEXT[
                          validation as ValidationTooltipText
                        ]
                      }
                      placement="top">
                      <IconContainer cx={css.icon} />
                    </Tooltip>
                  </div>
                }
                rawProps={{ 'data-testid': `validator-${validation}` }}
                value={validator === validation}
                onValueChange={() => {
                  setDraftValidator(validation as ValidationType);
                }}
              />
            </div>
            {validator === validation && !isBasicValidationType(validator) && (
              <TagsInput
                errorMessage={errors.answers}
                inputValue={inputValue}
                isDisabled={isNotAvailableStatus}
                isOptionSelected={validator === validation}
                pushToList={addCorrectAnswer}
                removeItemFromList={removeCorrectAnswer}
                tagsList={correctAnswers}
                updateInputValue={setInputValue}
              />
            )}

            {validation === 'PREDEFINED_OPTIONS' && <QuestionValidator />}
          </div>
        ))}
      </Panel>
    </InlineLabelInput>
  );
};
